import type { Idl, Program } from '@coral-xyz/anchor';
import { PublicKey, type TransactionInstruction } from '@solana/web3.js';
import type BN from 'bn.js';
import { TOKEN_PROGRAM_ID } from '../constants/programs.js';
import { deriveUsdcAta } from '../pda/ata.js';
import { deriveOutcomeMintPda, derivePlatformFeeVaultPda } from '../pda/derivations.js';
import type { TradeAccounts } from '../types/trading.js';
import { invokeMethod } from './common.js';

/** Shared current-contract account and argument construction for both trade directions. */
export function buildTradeInstruction(
  program: Program<Idl>,
  side: 'buy' | 'sell',
  params: { market: PublicKey; user: PublicKey; outcomeIndex: number },
  accounts: TradeAccounts,
  amount: BN,
  minimum: BN,
): Promise<TransactionInstruction> {
  const programId = program.programId;
  const marketSeed = params.market.toBuffer();
  const outcomeSeed = Buffer.from([params.outcomeIndex]);
  const [outcomeMint] = deriveOutcomeMintPda(params.market, params.outcomeIndex, programId);
  const claimRightsMint = tradePda(programId, 'claim_rights_mint', marketSeed, outcomeSeed);
  const platformFeeVault = derivePlatformFeeVaultPda(accounts.marketConfig, programId)[0];
  return invokeMethod(program, side, params.outcomeIndex, amount, minimum, 0)
    .accounts({
      trader: params.user,
      market: params.market,
      marketConfig: accounts.marketConfig,
      quoteMint: accounts.quoteMint,
      outcomeMint,
      claimRightsMint,
      outcomeMintAuthority: tradePda(programId, 'outcome_mint_authority', marketSeed),
      marketQuoteVaultAuthority: tradePda(programId, 'market_quote_vault_authority', marketSeed),
      marketQuoteVault: tradePda(programId, 'market_quote_vault', marketSeed),
      platformFeeVault,
      traderQuoteAccount: deriveUsdcAta(params.user, accounts.quoteMint),
      traderOutcomeAccount: deriveUsdcAta(params.user, outcomeMint),
      traderClaimRightsAccount: deriveUsdcAta(params.user, claimRightsMint),
      tokenProgram: TOKEN_PROGRAM_ID,
      outcomeTokenVault: tradePda(programId, 'outcome_token_vault', marketSeed, outcomeSeed),
      eventSchedule: accounts.eventSchedule ?? null,
      eventAuthority: tradePda(programId, '__event_authority'),
      program: programId,
    })
    .instruction();
}

export function tradePda(programId: PublicKey, seed: string, ...seeds: Buffer[]): PublicKey {
  return PublicKey.findProgramAddressSync([Buffer.from(seed), ...seeds], programId)[0];
}
